/**
 * TargetLifecycleMonitor — Panel 4 in the ability-debug family.
 *
 * Modernized with theme tokens, Badge primitives, and Button controls.
 * Powered by server `mobAttackDiagnostic` broadcasts (MobAISystem).
 */
import React, { useCallback, useState } from 'react';
import {
  useTargetLifecycleStream,
  serializeTargetGroupForCopy,
} from '../hooks/useTargetLifecycleStream';
import { gameLogger } from '../../../utils/GameLogger';
import { Button, Badge } from '../primitives';
import { COLORS, FONT_SIZE, FONT_WEIGHT, RADII, SPACE } from '../theme';
import { lifecycleStyles as styles } from './lifecycleStyles';

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour12: false });
}

export const TargetLifecycleMonitorContent: React.FC = () => {
  const { activeTargetId, group, paused, pause, resume, clear } = useTargetLifecycleStream();
  const [copied, setCopied] = useState(false);

  const copyAll = useCallback(() => {
    const text = serializeTargetGroupForCopy(group);
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      (err) => gameLogger.warn('ui', `[TargetLifecycleMonitor] clipboard write failed: ${String(err)}`),
    );
  }, [group]);

  const eventCount = group?.events.length ?? 0;

  return (
    <div style={styles.container}>
      <div style={styles.toolbar}>
        <div style={styles.toolbarLeft}>
          <Button
            variant={paused ? 'brass' : 'secondary'}
            size="xs"
            onClick={paused ? resume : pause}
          >
            {paused ? '▶ Resume' : '⏸ Pause'}
          </Button>
          <Button variant="ghost" size="xs" onClick={clear}>
            Clear
          </Button>
          <Button variant="secondary" size="xs" onClick={copyAll}>
            {copied ? '✓ Copied' : '📋 Copy'}
          </Button>
        </div>
        <Badge tone="neutral" size="sm">
          {eventCount} {eventCount === 1 ? 'event' : 'events'}
        </Badge>
      </div>

      <div style={{
        padding: '6px 10px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${COLORS.borderSubtle}`,
        borderRadius: RADII.sm,
        fontSize: FONT_SIZE.xs,
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.xs,
      }}>
        {activeTargetId ? (
          group ? (
            <>
              <span style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase' }}>TARGET:</span>
              <strong style={{ color: COLORS.textPrimary }}>{group.mobName}</strong>
              <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: 'monospace' }}>({group.groupId})</span>
            </>
          ) : (
            <span style={{ color: COLORS.textMuted, fontStyle: 'italic' }}>
              Target locked — waiting for first attack
            </span>
          )
        ) : (
          <span style={{ color: COLORS.textMuted, fontStyle: 'italic' }}>
            No target. Click a mob to lock.
          </span>
        )}
      </div>

      <div style={styles.list}>
        {!group || group.events.length === 0 ? (
          <div style={styles.empty}>
            {paused
              ? 'Paused — resume to capture events'
              : activeTargetId
                ? 'No events yet. Walk into the target\'s attack range.'
                : 'Lock a target first.'}
          </div>
        ) : (
          group.events
            .slice()
            .reverse()
            .map((e, i) => {
              const inRange = e.distance != null && e.range != null ? e.distance <= e.range : true;
              return (
                <div
                  key={`${e.time}-${i}`}
                  style={{
                    border: `1px solid ${COLORS.borderSubtle}`,
                    borderRadius: RADII.sm,
                    padding: '6px 8px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: COLORS.info, fontWeight: FONT_WEIGHT.bold, fontSize: 11, fontFamily: 'monospace' }}>
                      {e.category}:{e.label}
                    </span>
                    <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: 'monospace' }}>
                      {formatTime(e.time)} · +{e.time - group.startTime}ms
                    </span>
                  </div>

                  {e.detail && (
                    <div style={{ color: COLORS.textSecondary, fontSize: 11, fontFamily: 'monospace' }}>
                      {e.detail}
                    </div>
                  )}

                  {e.distance != null && e.range != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs, marginTop: 2 }}>
                      <Badge tone={inRange ? 'success' : 'danger'} size="sm">
                        {inRange ? '✓ RANGE' : '✗ OUT OF RANGE'}
                      </Badge>
                      <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: 'monospace' }}>
                        {e.distance}px / {e.range}px
                      </span>
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>
    </div>
  );
};
